import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../../Components/Request';
import { useAuth } from '../../../Components/AuthContext';
import hasPermission from '../../../Hooks/hasPermission';
import { ClientModes } from '../Clients/clientModes';

export const useClientDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const navRef = useRef(navigate);
  useEffect(() => {
    navRef.current = navigate;
  }, [navigate]);

  const { user: authUser } = useAuth();
  const perms = useMemo(() => {
    const p = authUser?.permissionsActives;
    return {
      canEditClient: hasPermission(p, 'CL_EDIT'),
      canViewUsers: hasPermission(p, 'US_VIEW'),
      canViewDeliveries: hasPermission(p, 'DL_VIEW'),
      canViewLoads: hasPermission(p, 'LD_VIEW'),
    };
  }, [authUser]);

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [usersList, setUsersList] = useState<{ id: string; name: string }[]>([]);
  const [busy, setBusy] = useState(false);

  const fetchClient = useCallback(async () => {
    if (!id) return;
    try {
      const res = await axiosInstance.get(`/clients/${id}`);
      setData(res.data);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 401) navRef.current('/login');
      else if (status === 404) navRef.current('/clients');
      else if (status === 403) navRef.current('/');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchClient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (perms.canViewUsers) axiosInstance.get('/users').then((r) => setUsersList(r.data || [])).catch(() => {});
  }, [perms.canViewUsers]);

  /**
   * Guarda nombre y modo del cliente.
   *
   * 📌 Se llamaba `editName` y ahora edita las dos cosas, así que pasó a `editClient`. El nombre viejo
   * habría dejado la impresión de que los modos se guardan en otra parte.
   *
   * ⚠️ Los modos van **siempre** en el cuerpo, aunque no se hayan tocado. El backend los declara
   * opcionales para que un `PUT` de solo nombre no los pise, pero desde esta pantalla el formulario
   * conoce el estado completo: mandarlo entero evita que un guardado parcial deje la pantalla mostrando
   * algo distinto de lo que quedó en la base.
   */
  const editClient = useCallback(
    async (name: string, modes: ClientModes): Promise<true | string> => {
      if (!id || !name.trim()) return 'Name is required';
      setBusy(true);
      try {
        await axiosInstance.put(`/clients/${id}`, { name: name.trim(), ...modes });
        await fetchClient();
        return true;
      } catch (err: any) {
        return err?.response?.data?.message || 'Failed to update client';
      } finally {
        setBusy(false);
      }
    },
    [id, fetchClient]
  );

  /**
   * Activa o desactiva al cliente.
   *
   * 🔑 Va aparte de `editClient` y no como una casilla más del modal: **desactivar no es editar**. Es una
   * decisión de operación con efecto inmediato en quién puede darle trabajo, y esconderla dentro del
   * formulario de renombrar la volvería un cambio que se hace de paso.
   *
   * 📌 Manda **solo** `active`. El backend declara los campos opcionales justo para esto, así que un
   * cambio de estado no puede pisar el nombre ni los modos por accidente.
   *
   * ⚠️ Desactivar **no** borra. El borrado es `remove()`, y desde que existe el borrado suave se puede
   * revertir; esto ni siquiera lo necesita, se vuelve a prender.
   */
  const setActive = useCallback(
    async (active: boolean): Promise<true | string> => {
      if (!id) return 'No client';
      setBusy(true);
      try {
        await axiosInstance.put(`/clients/${id}`, { active });
        await fetchClient();
        return true;
      } catch (err: any) {
        return err?.response?.data?.message || 'Failed to change the client state';
      } finally {
        setBusy(false);
      }
    },
    [id, fetchClient]
  );

  const remove = useCallback(async (): Promise<true | string> => {
    if (!id) return 'No client';
    setBusy(true);
    try {
      await axiosInstance.delete(`/clients/${id}`);
      navRef.current('/clients');
      return true;
    } catch (err: any) {
      return err?.response?.data?.message || 'Failed to delete client';
    } finally {
      setBusy(false);
    }
  }, [id]);

  const assignUser = useCallback(
    async (userId: string) => {
      if (!id || !userId) return;
      setBusy(true);
      try {
        await axiosInstance.post(`/user-reference/${id}`, { action: 'add', user_id: userId });
        await fetchClient();
      } finally {
        setBusy(false);
      }
    },
    [id, fetchClient]
  );
  const unassignUser = useCallback(
    async (userId: string) => {
      if (!id) return;
      setBusy(true);
      try {
        await axiosInstance.post(`/user-reference/${id}`, { action: 'remove', user_id: userId });
        await fetchClient();
      } finally {
        setBusy(false);
      }
    },
    [id, fetchClient]
  );

  return {
    perms,
    client: data?.client,
    loads: (data?.loads || []) as any[],
    userClients: (data?.userClients || []) as any[],
    deliveries: (data?.deliveries || []) as any[],
    usersList,
    loading,
    busy,
    editClient,
    setActive,
    remove,
    assignUser,
    unassignUser,
    goLoad: (loadId: string) => navRef.current(`/guide/detail/${loadId}`),
    goUser: (userId: string) => navRef.current(`/users/${userId}`),
    goDelivery: (deliveryId: string) => navRef.current(`/deliveries/${deliveryId}`),
  };
};
